/* eslint-disable react/no-danger */
import { useMutation } from "@tanstack/react-query";
import {
  Box,
  Button,
  Divider,
  DocumentEditIcon,
  Down1Icon,
  FormControl,
  FormControlLabel,
  FormLabel,
  GKButton,
  GKCheckbox,
  GKModal,
  GKRadio,
  GKSearchSelect,
  GKTextField,
  Grid2,
  LinkIcon,
  PhoneInput,
  RadioGroup,
  Show,
  Typography,
} from "components";
import { useFetchQuery } from "hooks/useQueries";
import { AdvisorOnboardFormInterface } from "interfaces/OnboardingInterface";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useAppSelector } from "store/store";
import { GET_ALL_CLIENT } from "utils/apiRoutes";
import axiosInstance from "utils/axiosInstance";
import { UserRoleTypes } from "utils/common";
import { QueryKeys } from "utils/queryKey";
import { emailRegex, validateNotWhitespaceOnly } from "utils/validationSchema";

const AdvisorOnboardingTypeSelect = ({ basketList }: { basketList: any[] }) => {
  const form = useFormContext<AdvisorOnboardFormInterface>();
  const userData = useAppSelector((state) => state?.user?.userData);
  const partnerList = useAppSelector((state) => state?.app?.partnerList);
  const [hasError, setHasError] = useState(false);
  const [showFeeDescription, setShowFeeDescription] = useState(false);
  const [showbasketDescription, setShowBasketDescription] = useState(false);

  const buttonData = [
    {
      title: "New / via KRA / Existing",
      description:
        "Seamlessly onboard new clients by manually entering details or fetching their data from KRA to begin their investment journey.",
      disable: false,
      icon: <DocumentEditIcon width={40} height={40} />,
      onClick: () => {
        form.setValue("type", "advisor");
      },
      value: "advisor",
    },
    {
      title: "Client Self-Onboarding",
      description:
        "Enable hassle-free onboarding by sending a secure link for clients to complete their compliances and investment setup",
      icon: <LinkIcon width={40} height={40} />,
      disable: false,
      onClick: () => {
        form.setValue("type", "client");
      },
      value: "client",
    },
  ];

  // ===========================================================================================
  const { isFetching, data: clients } = useFetchQuery({
    key: [QueryKeys.CLIENTS_SHORT_DATA],
    route: GET_ALL_CLIENT,
  });
  // ===========================================================================================
  const handleClientSelfOnboarding = useMutation({
    mutationKey: ["HANDLE_CLIENT_SEND_LINK_MUTATUION"],
    mutationFn: (values: AdvisorOnboardFormInterface) =>
      axiosInstance.post(`/client/self-onboarding/`, {
        basketId: values.basketObject?.id || null,
        feePlanId: values.selectedFeePlan?.id || null,
        partnerId: values.partner || userData?.partnerId,
        clientName: values.personalDetails.name,
        phone: values.phone,
        clientEmail: values.personalDetails.email,
        companyId: userData.companyId,
        skipAdvanceFee: values.skipAdvanceFee,
        clientId: values.clientId,
        mfEntity: values.mfEntity,
        onboardingType: values.onboardingType,
      }),
    onSuccess: () => {
      form.setValue("showSendClientCompletion", true);
    },
  });
  // ===========================================================================================
  const { mutate: getClientById } = useMutation({
    mutationKey: ["GET_CLIENT_BY_ID"],
    mutationFn: (selectedClients: any) =>
      axiosInstance.get(`/client/${selectedClients}/`),
    onSuccess: (response: any) => {
      form.reset({
        ...form.getValues(),
        ...response.data,
        isExistingClient: "trueValue",
        activeStep: form.getValues("activeStep"),
        onboardingType: form.getValues("onboardingType"),
        selectedFeePlan: response?.data?.basketIds[0]?.feePlan || null,
        basketObject: response?.data?.basketIds[0]?.basket || null,
        portfolioObject: response?.data?.basketIds[0]?.portfolio || null,
        mfInvestmentMode: response?.data?.dematAccounts?.length
          ? "demat"
          : "folio",
        type: form.getValues("type"),
        bankAccounts:
          response?.data?.bankAccounts?.length === 0
            ? [
                {
                  name: "",
                  accountNum: "",
                  type: "",
                  ifsc: "",
                  confirmAccountNum: "",
                },
              ]
            : response?.data?.bankAccounts,
        dematAccounts:
          response?.data?.dematAccounts?.length === 0
            ? [
                {
                  brokerName: "",
                  dematId: "",
                  uniqClientCode: "",
                  depositary: "",
                },
              ]
            : response?.data?.bankAccounts,
      });
    },
  });

  const onboardingTypeWatch = form.watch("onboardingType") || [];
  const isBasketFeePlanRequired = onboardingTypeWatch.includes("RIA");

  const isExistingClient = form.getValues("isExistingClient") === "trueValue";
  const basketOverview = form.getValues("basketObject.overview");
  const feePlanOverview = form.getValues("selectedFeePlan.description");

  return (
    <>
      <form
        id="advisor-onboarding-onboarding-form"
        onSubmit={form.handleSubmit((values) => {
          if (form.getValues("type") === "client") {
            handleClientSelfOnboarding.mutate(values);
            return;
          }
          form.setValue("activeStep", 1);
        })}
      >
        <Grid2 container spacing={4}>
          <Grid2 size={12} display={"flex"} alignItems={"center"} gap={2}>
            {buttonData?.map((dataItem, index) => {
              return (
                <GKButton
                  key={index}
                  disabled={dataItem.disable}
                  onClick={dataItem.onClick}
                  variant="outlined"
                  color="dark"
                  sx={{
                    borderRadius: 3,
                    background:
                      form.getValues("type") === dataItem.value
                        ? "var(--primary-50)"
                        : "",
                    borderColor:
                      form.getValues("type") === dataItem.value
                        ? "var(--primary-900)"
                        : "",
                  }}
                >
                  <Box
                    display={"flex"}
                    alignItems={"flex-start"}
                    flexDirection={"column"}
                    p={2}
                    gap={1}
                  >
                    <Typography fontWeight={600} textAlign={"start"}>
                      {dataItem.title}
                    </Typography>
                    <Typography
                      textAlign={"start"}
                      fontSize={12}
                      color="var(--neutral-500)"
                    >
                      {dataItem.description}
                    </Typography>
                  </Box>
                </GKButton>
              );
            })}
          </Grid2>
          <Show.When isTrue={!!form.getValues("type")}>
            <Grid2 container spacing={2} size={12}>
              <Grid2 size={12}>
                <Controller
                  name="isExistingClient"
                  control={form.control}
                  render={({ field }) => (
                    <FormControl>
                      <FormLabel>Client Type</FormLabel>
                      <RadioGroup
                        {...field}
                        row
                        value={field.value}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                        }}
                      >
                        <FormControlLabel
                          value="falseValue"
                          control={<GKRadio disableLeftPadding />}
                          label="New Client"
                        />
                        <FormControlLabel
                          value="trueValue"
                          control={<GKRadio disableLeftPadding />}
                          label="Existing Client"
                        />
                      </RadioGroup>
                    </FormControl>
                  )}
                />
              </Grid2>
              <Show.When isTrue={isExistingClient}>
                <Grid2 size={12}>
                  <Controller
                    name="clientId"
                    control={form.control}
                    rules={{
                      required: {
                        value: isExistingClient,
                        message: "Please select a client",
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <GKSearchSelect
                        disableClearable
                        inputLabel="Existing Client"
                        options={clients?.data || []}
                        disabled={isFetching}
                        requiredField
                        error={!!error}
                        helperText={!!error && error.message}
                        onChange={(e, val) => {
                          getClientById(val.id);
                          field.onChange(val ? val?.id : null);
                        }}
                        getOptionLabel={(option) => option.name || option}
                      />
                    )}
                  />
                </Grid2>
              </Show.When>
              <Grid2 size={12}>
                <Controller
                  name="personalDetails.name"
                  control={form.control}
                  rules={{
                    required: {
                      value: form.getValues("activeStep") === 1,
                      message: "Please enter name",
                    },
                    validate: validateNotWhitespaceOnly,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <GKTextField
                      {...field}
                      inputLabel="Enter Full Name"
                      requiredField
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid2>
              <Grid2 size={6}>
                <Controller
                  name="phone"
                  rules={{
                    required: {
                      value: form.getValues("activeStep") === 0,
                      message: "Please enter Mobile number",
                    },
                  }}
                  control={form.control}
                  render={({ field, fieldState: { error } }) => (
                    <PhoneInput
                      {...field}
                      requiredField
                      inputLabel="Mobile No."
                      onSelectChange={(event: any) => {
                        form.setValue("countryCode", event);
                      }}
                      onTextChange={(event: any) => {
                        field.onChange(event);
                      }}
                      onTextValue={field.value}
                      onSelectValue={form.getValues("countryCode")}
                      error={!!error || hasError}
                      helperText={error?.message}
                      hasError={hasError}
                      setHasError={setHasError}
                    />
                  )}
                />
              </Grid2>
              <Grid2 size={6}>
                <Controller
                  name="personalDetails.email"
                  control={form.control}
                  rules={{
                    required: {
                      value: form.getValues("activeStep") === 0,
                      message: "Please enter email",
                    },
                    pattern: {
                      value: emailRegex,
                      message: "Please enter a valid mail",
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <GKTextField
                      {...field}
                      inputLabel="Email Id"
                      requiredField
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid2>
              <Show.When isTrue={userData?.userRole === UserRoleTypes.advisor}>
                <Grid2 size={6}>
                  <Controller
                    name="partner"
                    control={form.control}
                    render={({ field }) => (
                      <GKSearchSelect
                        disableClearable={false}
                        inputLabel="Partner"
                        options={partnerList || []}
                        onChange={(e, val) => {
                          field.onChange(val.value);
                        }}
                        getOptionLabel={(option) => option.name || option}
                      />
                    )}
                  />
                </Grid2>
              </Show.When>
            </Grid2>
            <Grid2 size={12}>
              <Divider />
            </Grid2>
            <Grid2 size={12}>
              <Controller
                name="onboardingType"
                control={form.control}
                defaultValue={[]}
                rules={{
                  required: {
                    value: true,
                    message: "",
                  },
                }}
                render={({ field, fieldState: { error } }) => {
                  const handleChange = (value: any) => {
                    if (!field.value) {
                      field.onChange([value]);
                      return;
                    }
                    if (field.value?.includes(value)) {
                      field.onChange(field.value.filter((v) => v !== value));
                    } else {
                      field.onChange([...field.value, value]);
                    }
                  };

                  return (
                    <FormControl>
                      <FormLabel
                        required
                        style={{
                          color: error ? "red" : "",
                        }}
                      >
                        Onboarding Type
                      </FormLabel>
                      <Box display={"flex"}>
                        <FormControlLabel
                          label="RIA Compliant"
                          control={
                            <GKCheckbox
                              checked={field.value?.includes("RIA")}
                              onChange={() => handleChange("RIA")}
                            />
                          }
                        />
                        <Show.When isTrue={userData?.mfOnboarding === true}>
                          <FormControlLabel
                            label="MF Readiness"
                            control={
                              <GKCheckbox
                                checked={field.value?.includes("MF")}
                                onChange={() => handleChange("MF")}
                              />
                            }
                          />
                        </Show.When>
                      </Box>
                    </FormControl>
                  );
                }}
              />
            </Grid2>
            <Show.When isTrue={onboardingTypeWatch.includes("MF")}>
              <Grid2 size={12}>
                <Controller
                  name="mfEntity"
                  rules={{
                    required: {
                      value: onboardingTypeWatch.includes("MF"),
                      message: "",
                    },
                  }}
                  control={form.control}
                  render={({ field, fieldState: { error } }) => (
                    <FormControl>
                      <FormLabel
                        required
                        style={{
                          color: error ? "red" : "",
                        }}
                      >
                        MF Entity
                      </FormLabel>
                      <RadioGroup
                        {...field}
                        row
                        value={field.value}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                        }}
                      >
                        <FormControlLabel
                          value="RIA"
                          control={<GKRadio />}
                          label="RIA"
                        />
                        <FormControlLabel
                          value="MF"
                          control={<GKRadio />}
                          label="MF"
                        />
                      </RadioGroup>
                    </FormControl>
                  )}
                />
              </Grid2>
            </Show.When>
            <Grid2 size={12}>
              <Controller
                name="basketObject"
                control={form.control}
                rules={{
                  required: {
                    value: isBasketFeePlanRequired,
                    message: "Please select a basket",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <GKSearchSelect
                      disableClearable
                      inputLabel="Select Basket"
                      options={basketList || []}
                      disabled={isFetching}
                      requiredField={isBasketFeePlanRequired}
                      error={!!error}
                      helperText={!!error && error.message}
                      value={field.value || null}
                      onChange={(e, val) => {
                        field.onChange(val);
                        form.setValue("selectedFeePlan", null);
                      }}
                      getOptionLabel={(option) => option.name || option}
                    />

                    {/* Button only after basket selected */}
                    {field.value && (
                      <Button
                        endIcon={<Down1Icon />}
                        onClick={() => {
                          setShowBasketDescription(true);
                        }}
                      >
                        Show Basket Overview
                      </Button>
                    )}
                  </>
                )}
              />
            </Grid2>
            <Grid2 size={12}>
              <Controller
                name="selectedFeePlan"
                control={form.control}
                rules={{
                  required: {
                    value: isBasketFeePlanRequired,
                    message: "Please select a fee plan",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <GKSearchSelect
                      disableClearable
                      inputLabel="Select Fee Plan"
                      options={
                        form.getValues("basketObject.feePlansList") || []
                      }
                      disabled={isFetching}
                      value={field.value || null}
                      onChange={(e, val) => {
                        field.onChange(val);
                      }}
                      getOptionLabel={(option) => option.name || option}
                      requiredField={isBasketFeePlanRequired}
                      error={!!error}
                      helperText={!!error && error.message}
                    />

                    {/* Show button only after fee plan selected */}
                    {field.value && (
                      <Button
                        endIcon={<Down1Icon />}
                        onClick={() => {
                          setShowFeeDescription(true);
                        }}
                      >
                        Show Fee Plan Description
                      </Button>
                    )}
                  </>
                )}
              />
            </Grid2>
          </Show.When>
        </Grid2>
      </form>
      <GKModal
        open={showFeeDescription || showbasketDescription}
        setOpen={
          showFeeDescription ? setShowFeeDescription : setShowBasketDescription
        }
        modalTitle={
          showFeeDescription ? "Fee Plan Description" : "Basket Overview"
        }
      >
        <>
          {showFeeDescription && (
            <div
              dangerouslySetInnerHTML={{
                __html: feePlanOverview,
              }}
            />
          )}
          {showbasketDescription && (
            <div
              dangerouslySetInnerHTML={{
                __html: basketOverview,
              }}
            />
          )}
        </>
      </GKModal>
    </>
  );
};

export default AdvisorOnboardingTypeSelect;
